import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import VisionDiagram from "./VisionDiagram";

const WIVI_SLIDES = [
  {
    src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/ylhpopxz_WhatsApp%20Image%202025-03-11%20at%2011.35.42%20%282%29.jpg",
    alt: "Niña usando WIVI Vision — Sistema 3D de terapia visual",
    caption: "Terapia inmersiva en 3D",
    fit: "cover",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/r73g8a2j_Manuel%20Wivi.jpg",
    alt: "Niño concentrado jugando en WIVI Vision",
    caption: "Juegos que entrenan la visión",
    fit: "cover",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/6av4hv2t_Bienvenido%20a%20Wivi%20VIsion.jpg",
    alt: "Bienvenidos al mundo WIVI Vision",
    caption: "Mejora tu visión con juegos inmersivos en 3D",
    fit: "contain",
    bg: "#2a1a35",
  },
];

const VisionIntegral = () => {
  const [slide, setSlide] = useState(0);
  const timerRef = useRef(null);
  const total = WIVI_SLIDES.length;

  // Autoplay cada 5s
  useEffect(() => {
    timerRef.current = setInterval(() => setSlide((s) => (s + 1) % total), 5000);
    return () => clearInterval(timerRef.current);
  }, [total]);

  // Reset timer al interactuar manualmente
  const goTo = (i) => {
    setSlide(i);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setSlide((s) => (s + 1) % total), 5000);
    }
  };
  const prev = () => goTo((slide - 1 + total) % total);
  const next = () => goTo((slide + 1) % total);

  return (
    <section className="py-24 px-6" style={{ background: "var(--bg)" }} data-testid="vision-integral-section">
      <div className="max-w-6xl mx-auto">
        {/* Título */}
        <div className="max-w-3xl">
          <span className="section-eyebrow">Enfoque clínico</span>
          <h2 className="section-title mt-4">
            La visión es mucho más que <span className="handwritten">ver</span>
          </h2>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 text-lg leading-relaxed">
            Ver bien implica integrar cuatro sistemas que trabajan en equipo: el equilibrio, el lenguaje y la audición, la orientación espacial y el reconocimiento de lo que nos rodea. Por eso nuestra evaluación es integral — no miramos sólo los ojos, miramos cómo funciona todo el sistema.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-14 items-start">
          {/* Diagrama Venn SVG personalizado */}
          <div className="relative">
            <div
              className="rounded-3xl overflow-hidden p-4 md:p-6"
              style={{
                background: "var(--cream)",
                border: "1px solid var(--sage-100)",
                boxShadow: "0 20px 40px -25px rgba(54,66,32,0.25)",
              }}
            >
              <VisionDiagram />
            </div>
            <div className="absolute -top-4 -right-4 font-display text-5xl hidden md:block" style={{ color: "var(--sage-400)" }}>✦</div>
          </div>

          {/* Explicación de cada área */}
          <div className="space-y-5">
            <div className="soft-card" style={{ padding: "1.5rem" }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#bfcaa4" }}></span>
                <h3 className="font-medium text-lg" style={{ color: "var(--ink)" }}>Antigravedad · ¿Dónde estoy?</h3>
              </div>
              <p className="font-sans-body text-sm text-[color:var(--ink-soft)]">Cómo nos situamos en el espacio, postura, equilibrio. Afecta a la coordinación y al movimiento.</p>
            </div>
            <div className="soft-card" style={{ padding: "1.5rem" }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#c6c080" }}></span>
                <h3 className="font-medium text-lg" style={{ color: "var(--ink)" }}>Lenguaje y audición</h3>
              </div>
              <p className="font-sans-body text-sm text-[color:var(--ink-soft)]">Cómo vemos lo que oímos y leemos. Clave en el aprendizaje, la lectura y la comunicación.</p>
            </div>
            <div className="soft-card" style={{ padding: "1.5rem" }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#8ea067" }}></span>
                <h3 className="font-medium text-lg" style={{ color: "var(--ink)" }}>Centrado · ¿Dónde está?</h3>
              </div>
              <p className="font-sans-body text-sm text-[color:var(--ink-soft)]">Localización y seguimiento de objetos. Permite los movimientos oculares precisos al leer o en deporte.</p>
            </div>
            <div className="soft-card" style={{ padding: "1.5rem" }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#7a987e" }}></span>
                <h3 className="font-medium text-lg" style={{ color: "var(--ink)" }}>Identificación · ¿Qué es?</h3>
              </div>
              <p className="font-sans-body text-sm text-[color:var(--ink-soft)]">Reconocer e interpretar lo que vemos. Fundamental en memoria visual y comprensión.</p>
            </div>
          </div>
        </div>

        {/* ==== BANDA WIVI con carrusel ==== */}
        <div className="mt-20 grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden" style={{ boxShadow: "0 30px 60px -25px rgba(54,66,32,0.35)" }} data-testid="wivi-highlight">
          {/* Carrusel de fotos reales */}
          <div className="relative min-h-[360px] overflow-hidden" data-testid="wivi-carousel" style={{ background: WIVI_SLIDES[slide].bg || "var(--sage-700)", transition: "background 700ms ease" }}>
            {WIVI_SLIDES.map((s, i) => (
              <img
                key={i}
                src={s.src}
                alt={s.alt}
                className="absolute inset-0 w-full h-full transition-opacity duration-700"
                style={{
                  opacity: slide === i ? 1 : 0,
                  filter: "saturate(1.1)",
                  objectFit: s.fit || "cover",
                  padding: s.fit === "contain" ? "1.5rem" : 0,
                }}
                data-testid={`wivi-slide-${i}`}
              />
            ))}

            {/* Badge */}
            <div
              className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans-body text-xs font-semibold backdrop-blur-md z-10"
              style={{ background: "rgba(250,247,238,0.95)", color: "var(--sage-700)" }}
            >
              <Sparkles size={12} /> Tecnología real en nuestro centro
            </div>

            {/* Caption inferior */}
            <div className="absolute inset-x-0 bottom-0 z-10 px-5 pb-14 pt-10 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(54,66,32,0) 0%, rgba(54,66,32,0.7) 100%)" }}>
              <div className="font-sans-body text-xs uppercase tracking-widest mb-0.5" style={{ color: "var(--sage-200)" }}>WIVI Vision</div>
              <div className="font-display text-lg md:text-xl" style={{ color: "var(--cream)", textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
                {WIVI_SLIDES[slide].caption}
              </div>
            </div>

            {/* Flechas de navegación */}
            <button
              onClick={prev}
              aria-label="Anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-md transition-transform hover:scale-110 z-10"
              style={{ background: "rgba(250,247,238,0.85)", color: "var(--sage-700)" }}
              data-testid="wivi-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              aria-label="Siguiente"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-md transition-transform hover:scale-110 z-10"
              style={{ background: "rgba(250,247,238,0.85)", color: "var(--sage-700)" }}
              data-testid="wivi-next"
            >
              <ChevronRight size={18} />
            </button>

            {/* Dots indicadores */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid="wivi-dots">
              {WIVI_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Ir a foto ${i + 1}`}
                  className="rounded-full transition-all"
                  style={{
                    background: slide === i ? "var(--cream)" : "rgba(250,247,238,0.5)",
                    width: slide === i ? "24px" : "8px",
                    height: "8px",
                  }}
                  data-testid={`wivi-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Contenido */}
          <div className="p-8 md:p-12" style={{ background: "var(--sage-700)", color: "var(--cream)" }}>
            <span className="section-eyebrow" style={{ color: "var(--sage-200)" }}>Tecnología punta</span>
            <h3 className="section-title mt-3" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "var(--cream)" }}>
              WIVI Vision · entrenamiento visual con <span className="handwritten" style={{ color: "var(--sage-200)" }}>inteligencia artificial</span>
            </h3>
            <p className="font-sans-body mt-5 opacity-95 leading-relaxed">
              Juegos inmersivos en 3D que evalúan y entrenan más de 100 parámetros de la función visual. Evaluación en 12 minutos, recuperación visible en menos de 3 meses.
            </p>
            <ul className="mt-6 space-y-2 font-sans-body text-sm">
              <li className="flex items-start gap-2"><span style={{ color: "var(--sage-200)" }}>✦</span> Efectividad 100% sin regresión</li>
              <li className="flex items-start gap-2"><span style={{ color: "var(--sage-200)" }}>✦</span> Ideal para niños y adolescentes</li>
              <li className="flex items-start gap-2"><span style={{ color: "var(--sage-200)" }}>✦</span> Complemento perfecto a la terapia visual clásica</li>
            </ul>
            <Link
              to="/servicios#wivi"
              className="inline-flex items-center gap-2 mt-8 rounded-full px-6 py-3 font-medium"
              style={{ background: "var(--cream)", color: "var(--sage-700)" }}
              data-testid="wivi-learn-more"
            >
              Saber más sobre WIVI <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionIntegral;
